/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../RangeFilterController',
	'../RangeFilterModel',
	'../../../apiHelpers/SlotAPIHelper',
	'underscore'
], function(BaseClass, FilterModel, SlotAPIHelper, _) {
	'use strict';


	var RangeFilterController = BaseClass.extend({

		init: function(options) {
			this.id = _.uniqueId('RangeLocalFilterController');
			this.itemContext = options.itemContext;
			//checking if the aggregationType is defined in the itemContext. If not, add it. This is done so that if a local filter is added, when the user clicks on the slot action contextual menu and clicks filter, the added filter is perserved and shown. The itemContext here is local to this file, hence we can change it,
			this.itemContext.aggregationType || (Object.assign(this.itemContext, { aggregationType: options.aggregationType }));
			this.dashboardApi = options.dashboardApi;
			this.visualization = options.visualization;
			this.queryHelper = this._createQueryHelper();
			this.commonView = options.commonView;
			this.logger = options.logger;
			this.viewConfig = {
				commonView: this.commonView,
				dashboardApi: this.dashboardApi,
				logger: this.logger
			};
			this.localFilters = this.visualization.getLocalFilters();
			// TODO: Need to revisit
			this.datasetId = (options.slot && options.slot.getDefinition().getDatasetIdList()[0]) || this._firstDefinedDatasetId();
		},

		/**
		 * If this is a range filter that is in the local filters area, the range (and min/max) will be against
		 * 1) the first layer (dataset) where the range filter column is projected in that layer, or
		 * 2) the first layer (dataset) of the viz with data items if the range filter column is not projected in any layers, or
		 * 3) the first layer (dataset) of the viz if no data items in any layers. 
		 */
		_firstDefinedDatasetId: function() {
			let datasetsWithFilterColumnProjected;
			let datasetsWithDataItems = [];
			_.find(this.visualization.getSlots().getMappingInfoList(), slotInfo => {
				let slotDatasetIds = slotInfo.slot.getDefinition().getDatasetIdList();
				datasetsWithDataItems.push(...slotDatasetIds);
				if (slotInfo.dataItem.getColumnId() === this.itemContext.itemId) {
					datasetsWithFilterColumnProjected = slotDatasetIds;
					return true;
				}
			});

			let datasetIdToUse;
			// 1) Range filter column is projected in at least one layer, return the first layer id
			if (datasetsWithFilterColumnProjected && datasetsWithFilterColumnProjected.length > 0) {
				datasetIdToUse = datasetsWithFilterColumnProjected[0];
			} else {
				// 2) Range filter column is not projected in any layer, return the first layer with data items.
				datasetsWithDataItems = _.uniq(datasetsWithDataItems);
				if (datasetsWithDataItems.length > 0) {
					datasetIdToUse = datasetsWithDataItems[0];
				} else {
					// 3) No data slots with data items.... return the default layer id
					datasetIdToUse = this.visualization.getDefinition().getDatasetList()[0].id;
				}
			}

			return datasetIdToUse;
		},

		_createFilterModel: function() {
			var modelOptions = {
				name: this.itemContext.itemName
			};
			this.model = new FilterModel(modelOptions);
		},

		_initializeExtraData: function () {},

		_loadMinMax:function(){
			return this._getMinMax().then(function(minMaxArray) {
				this._refreshModel(minMaxArray);
			}.bind(this)).catch(function(error) {
				this.processError(error);
			}.bind(this));
		},

		/**
		 * Invoked when the following value changes.
		 * - fromValue, toValue, and inverted
		 * - also filterSelectionType which we don't support for now.
		 */
		onChange: function(payload) {
			const inverted = payload.inverted,
				from = payload.fromValue,
				to = payload.toValue;
			this.model.update(from, to, inverted);
		},

		/**
		 * Control Callback handler - update localFilter with current filter model
		 */
		onDone: function() {
			if ( this.model.min !== this.model.from || this.model.max !== this.model.to || this.model.inverted) {
				const filterContext = {
					exclude: this.model.inverted,
					from: this.model.from,
					to: this.model.to,
					aggregationType: this.itemContext.aggregationType
				};
				if(this.localFilters.filterExists(this.itemContext)) {
					this.localFilters.editFilter(this.itemContext, filterContext);
				} else {
					this.localFilters.addFilter(this.itemContext, filterContext);
				}
			} else {
				this.localFilters.removeFilter(this.itemContext);
			}
			if (!this.view.isCompact) {
				this.view.remove();
			}
			if(this.commonView.remove){
				this.commonView.remove();
			}
		},

		_refreshModel: function(minMaxArray) {
			if (!this.model) {
				return;
			}

			const min = minMaxArray[0];
			const max = minMaxArray[1];
			let from = min;
			let to = max;
			let inverted = false;

			const filter = this._getFilter();
			if (filter && filter.values && filter.values.length) {
				from = filter.values[0].u;
				to = filter.values[1].u;
				inverted = filter.operator.indexOf('not') >= 0 ? true : false;
			}
			this.view.resetView(/*skipRender*/true); //Force reset view because UI control is not handling min/max change gracefully.
			this.model.reset(min, max, from, to, inverted);
		},

		_getFilter: function() {
			return _.find(this.localFilters.getFilterList(), filter => {
				return filter.columnId === this.itemContext.itemId &&
					filter.aggregationType === this.itemContext.aggregationType;
			});
		},

		_getMinMax: function() {
			const queryService = this.dashboardApi.getFeature('QueryService');
			const query = queryService.createQuery(queryService.TYPE.MINMAX);

			return query.setDataSource(this.itemContext.sourceId).then(() => {
				this._buildQuery(query);

				return query.executeQuery().then(result => {
					const dataPointValueList = result.getDataPointValueList();
					return dataPointValueList.length ? _.pluck(dataPointValueList[0].pt, 'v') : [];
				});
			});
		},

		//ideally, all of this should be moved to the QueryService.
		_buildQuery: function(query){
			const datasetSlots = _.filter(this.visualization.getSlots().getSlotList(), slot => {
				return slot.getDefinition().getDatasetIdList().includes(this.datasetId);
			});

			const filter = this._getFilter();
			let queryDataItems = [];
			_.each(datasetSlots, (slot) => {
				// Add the query data items to query
				// loop through the slots to check every dataItem if they are valid as query data item
				slot.getDataItemList().forEach((dataItem, dataItemIndex) => {
					const subType = slot.getDefinition().getSubType();
					const dataItemType = subType === 'latitude' || subType === 'longitude' ? 'attribute' : dataItem.getType();
					const isValidDataItem = (dataItemType === 'attribute' ||
						dataItem.getId() === this.itemContext.uniqueId) &&
						!dataItem.getBinning() &&
						!SlotAPIHelper.isMultiMeasureSeriesDataItem(slot, dataItemIndex);
					if (isValidDataItem) {
						// add valid dataItem to queryDataItems one by one
						queryDataItems = query.addColumns([dataItem.getColumnId()]);
						// addColumns returns all the dataItems have been added to the queryDataItems
						let queryDataItem = queryDataItems[queryDataItems.length - 1];
						queryDataItem.setType(dataItemType);
						const contextUniqueId = this.itemContext.uniqueId;
						if (contextUniqueId === dataItem.getId()) {
							const aggregation = dataItem.getAggregation();
							queryDataItem.setAggregation(aggregation);
						} else {
							queryDataItem.setAggregation(null);
						}
					}
				});
			});

			// if the item is not projected, add a column to the query
			if (!this.itemContext.uniqueId) {
				queryDataItems = query.addColumns([ this.itemContext.itemId ]);
			}

			//if a filter is defined, add that to the query
			if(filter){
				query.addFilters(filter);
			}

			// Only the data item requesting minmax values should be pushed to projections,
			// so there is only one projection for minmax query for the filter item.
			// if the item we're getting the range for is visible, its uniqueId can be set here.
			_.each(queryDataItems, dataItem => {
				const projected = dataItem.getId() === this.itemContext.uniqueId || (dataItem.getColumnId() === this.itemContext.itemId && dataItem.getAggregation());
				dataItem.setProjected(projected);
			});
		},
		onClose: function(){
			RangeFilterController.inherited('onClose', this, arguments);
			if(this.commonView.remove){
				this.commonView.remove();
			}
		},

		_getMaximumFractionDigits: function() {
			let maximumFractionDigits = 3; // arbitrary default value 3.
			const mapping = this.visualization.getSlots().getMappingInfo(this.itemContext.uniqueId);
			const info = mapping && mapping.dataItem.getFormat();

			if (info && info.formatSpec && info.formatSpec.maximumFractionDigits !== undefined) {
				maximumFractionDigits = info.formatSpec.maximumFractionDigits;
			}
			return maximumFractionDigits;
		}
	});
	return RangeFilterController;
});
